'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface MorphingTextProps {
  texts: string[]
  className?: string
  duration?: number
  style?: React.CSSProperties
}

export function MorphingText({ texts, className = '', duration = 3000, style }: MorphingTextProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (texts.length === 0) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % texts.length)
    }, duration)

    return () => clearInterval(interval)
  }, [texts.length, duration])

  if (texts.length === 0) return null

  // Show first text immediately on server-side render
  if (!isClient) {
    return (
      <span className={`relative inline-block ${className}`} style={{ minHeight: '1.4em', minWidth: '100%', paddingBottom: '0.1em', ...style }}>
        {texts[0]}
      </span>
    )
  }

  // Extract gradient classes if present
  const hasGradient = className.includes('bg-gradient') && className.includes('bg-clip-text')
  const baseClasses = className.split(' ').filter(c => !c.includes('bg-gradient') && !c.includes('bg-clip-text') && !c.includes('text-transparent')).join(' ')
  const gradientClasses = className.split(' ').filter(c => c.includes('bg-gradient') || c.includes('bg-clip-text') || c.includes('text-transparent')).join(' ')

  return (
    <span className={`relative inline-block ${baseClasses}`} style={{ minHeight: '1.4em', minWidth: '100%', paddingBottom: '0.1em' }}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentIndex}
          initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={{ opacity: 0, y: -20, filter: 'blur(8px)' }}
          transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1],
          }}
          className={`inline-block ${gradientClasses}`}
          style={{ lineHeight: '1.2', ...style }}
        >
          {texts[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

